<template>
  <div ref="verification" class="verification" redesign>
    <!-- Redirect to home if not logged in -->
    <template v-if="userIsLoggedIn">{{ this.$router.push({ name: 'Home' }) }}</template>

    <!-- Header -->
    <StepProgressBar step2 />

    <div v-if="showVerification" class="verify-form form-container">
      <form @submit.prevent="handleSubmit">
        <!-- Employee ID -->
        <FormTextField
          v-if="isEmployeeCode"
          label="Enter your unique ID"
          :tabIndex="1"
          :value.sync="employeeId"
          :helpText="organizationDetails.uniqueIdHelpCopy"
          :helpTextTranslate="false"
          :showErrorMessage="hasSubmitted"
          :errorMessage="errors"
        />
        <!-- Pinwheel CTA -->
        <div
          v-if="isPinwheelApi"
          class="form-field"
          style="margin-bottom: 1rem"
        >
          <h4 class="form-field-header" v-translate>Verify Eligibility</h4>
          <div class="form-header">
            <p v-translate>
              Use your HR / payroll provider login to connect to the
              {{ organizationDetails.programName }}.
            </p>
          </div>
        </div>

        <!-- Submit -->
        <div class="button-container">
          <!-- Employee code -->
          <button
            v-if="isEmployeeCode"
            type="submit"
            tabindex="2"
            class="form-button"
            :class="{ 'disabled-button': disabled }"
            :disabled="disabled"
          >
            <span v-if="!loading" key="verify" v-translate>Verify my ID</span>
            <span v-else-if="loading">
              <LoadSpinner color="white" />
            </span>
          </button>

          <!-- Pinwheel -->
          <button
            v-else
            type="submit"
            tabindex="2"
            class="form-button"
            :class="{ 'disabled-button': disabled }"
            :disabled="disabled"
          >
            <span v-if="!loading" key="verify" v-translate>Connect</span>
            <span v-else-if="loading">
              <LoadSpinner color="white" />
            </span>
          </button>
        </div>
        <div class="verify-form-footer form-footer">
          <span class="sign-in-text">
            <span v-translate>Been here before?</span>&nbsp;
            <router-link :to="{ name: 'Login' }" tabindex="3">
              <span v-translate>Sign in</span>
            </router-link>
          </span>
        </div>
      </form>
    </div>

    <!-- Sub-org select -->
    <template v-else>
      <div class="form-container">
        <div class="sub-org-select-form-header form-header">
          <h4 v-translate>Please select the company you work for:</h4>
        </div>
        <div class="sub-org-select-form">
          <div v-for="subOrg in organizationDetails.subOrganizations" class="sub-org" @click="subOrgClickHandler(subOrg.id)">
            <img v-if="subOrg.logo" :src="subOrg.logo" class="sub-org-logo" />
            <!-- Local development only -->
            <!-- <img v-if="subOrg.logo" :src="subOrgLogoURL(subOrg.logo)" class="sub-org-logo" /> -->
            <span v-else>{{ subOrg.name }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import EmployeeCode from '@/services/employeeCodes'
import StepProgressBar from '@/components/stepper/StepProgressBar'
import FormTextField from '@/components/forms/FormTextField'
import { analyticsMixin } from '@/services/googleAnalytics'
import PinwheelApi from '@/services/pinwheel'
import UserEvent from '@/services/userEvents'

export default {
  name: 'Verification',
  mixins: [analyticsMixin],
  components: {
    StepProgressBar,
    LoadSpinner,
    FormTextField,
  },
  data() {
    return {
      employeeId: '',
      verificationFailed: null,
      loading: false,
      errors: null,
      hasSubmitted: false,
      showHelp: false,
      lockout: false,
      pinwheelLoggedIn: false,
      pinwheelAccountId: null,
      selectedSubOrg: null,
    }
  },
  beforeMount() {
    // If the user is logged in redirect to the home page
    if (this.userIsLoggedIn) {
      this.$router.push({ name: 'Home' })
    }

    // Check for employeeCode on route params
    let code = this.$route.query.code
    if (code) {
      this.employeeId = code
      this.verifyEmployee(code)
    }
  },
  methods: {
    clickedHelp() {
      // if showing, close and remove event listener
      // if not showing, open and add event listener
      if (!this.showHelp) {
        this.showHelp = true
        setTimeout(
          () => this.$refs.verification.addEventListener('click', this.helpTextCallBack),
          0,
        )
      }
    },
    helpTextCallBack() {
      this.showHelp = false
      this.$refs.verification.removeEventListener('click', this.helpTextCallBack)
    },
    handleSubmit() {
      this.hasSubmitted = true

      if (this.isEmployeeCode)
        this.verifyEmployee(this.employeeId)

      if (this.isPinwheelApi) this.handlePinwheelModal()
    },
    async verifyEmployee(id) {
      this.loading = true
      this.errors = null
      this.trackClickedCTA()
      const employeeCode = await EmployeeCode.api.verifyEmployeeId(id).catch(err => {
        this.eventsTracker('employee-id-submission', err.response.data.detail)
        if (err.response.data.attempts >= 5) {
          this.lockout = true
          return
        }
        try {
          // TODO: alter error_lookup to work as an actual mapping
          this.errors =
            this.error_lookup[err.response.data.detail](this.organizationDetails) +
            (err.response.data.detail == 'Invalid code provided.'
              ? `<br><br> ${this.$gettext('You have')} ${5 -
                  err.response.data.attempts} ${this.$gettext('more tries to enter a valid ID.')}`
              : '')
        } catch {
          this.errors = this.$gettext('There was a problem. Please try again later.')
        }
      })

      /* If verification didn't fail, route to account creation page, passing along employee code */
      if (!this.errors) {
        this.$router.push({
          name: 'Registration',
          params: { code: employeeCode, subOrgId: this.selectedSubOrg?.id },
        })
      } else {
        this.loading = false
      }
    },
    handlePinwheelLinkEvent(name, payload) {
      switch (name) {
        case 'open':
          // Link modal now open, hide the spinner
          this.loading = false
          break
        case 'login':
          // Account successfully logged in
          this.pinwheelLoggedIn = true
          this.pinwheelAccountId = payload.accountId
          break
        case 'exit':
          // Link modal now closed
          if (this.pinwheelLoggedIn) {
            // Login successful, redirect to the registration page
            this.$router.push({
              name: 'Registration',
              params: { code: this.pinwheelAccountId, subOrgId: this.selectedSubOrg?.id },
            })
          } else {
            // Unsuccessful login, show error
          }
          break
        default:
          break
      }
    },
    async handlePinwheelModal() {
      // Opening link modal, show the spinner
      this.loading = true

      // Get a Pinwheel link token, then open the modal
      let link_token = await PinwheelApi.getVerificationLinkToken(this.$language.current, this.selectedSubOrg ? this.selectedSubOrg.id : null)
      if (link_token) {
        Pinwheel.open({ linkToken: link_token.token, onEvent: this.handlePinwheelLinkEvent })
      }
    },
    subOrgClickHandler(sub_org_id) {
      this.selectedSubOrg = this.organizationDetails.subOrganizations.find(subOrg => subOrg.id == sub_org_id) || null
    },
    /* Local development only */
    /*
    subOrgLogoURL(logoURL) {
      return "media" + logoURL.split("/media")[1]
    },
    */
    isVerificationMethod(method_name) {
      return ((!this.organizationDetails.subOrgVerificationMethodOverride && this.organizationDetails.verificationMethod == method_name) ||
        (this.organizationDetails.subOrgVerificationMethodOverride && this.selectedSubOrg?.verificationMethod == method_name))
    },
  },
  checkIfSubmitViaEnter(e) {
    if (e.key == 'Enter') {
      this.verifyEmployee(this.employeeId)
    }
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars', 'currentLanguage']),
    ...mapGetters(['userIsLoggedIn']),
    disabled() {
      if (this.lockout) {
        this.errors = this.error_lookup['No more attempts allowed'](this.organizationDetails)
        this.employeeId = ''
        return true
      }

      // If using Pinwheel, always allow the button to be
      if (this.isPinwheelApi) return false

      return !this.employeeId
    },

    error_lookup() {
      const translated = [
        this.$gettext('It appears you have already created an account.'),
        this.$gettext('Please sign in.'),
      ]
      const invalidCode = [
        this.$gettext('Sorry, you do not appear to be eligible to participate in the'),
        this.$gettext(
          'Grant Circle. If you have questions about eligibility, or believe you are eligible to participate, please email',
        ),
      ]

      const invalidAndInactiveGenerator = org =>
        `${invalidCode[0]} ${org.name}${this.currentLanguage == 'en' ? ' ' : ''}${
          invalidCode[1]
        } ${org.supportEmail}.`

      const exists = () => `${translated[0]} <a href="/login">${translated[1]}</a>`
      const inactive = invalidAndInactiveGenerator
      const invalid = invalidAndInactiveGenerator

      return {
        'Invalid code provided.': invalid,
        'El código que ingresó es inválido.': invalid,
        'An account has already been created with this unique ID.': exists,
        'Ya existe una cuenta con este ID único.': exists,
        'Inactive code provided.': inactive,
        'El código que ingresó está inactivo.': inactive,
        'No more attempts allowed': () =>
          this.$gettext(
            'You have reached the limit for invalid verification attempts.  You have been locked out for one hour.  Try again later.',
          ),
      }
    },
    isEmployeeCode() {
      return this.isVerificationMethod('EMPLOYEE_CODE')
    },
    isPinwheelApi() {
      return this.isVerificationMethod('PINWHEEL_API')
    },
    showVerification() {
      return (!this.organizationDetails.subOrgVerificationMethodOverride || (this.organizationDetails.subOrgVerificationMethodOverride && this.selectedSubOrg))
    },
  },
  mounted() {
    // Load external Pinwheel API script
    let pinwheel_api_script = document.createElement('script')
    pinwheel_api_script.setAttribute('src', 'https://cdn.getpinwheel.com/pinwheel-v2.3.js')
    document.head.appendChild(pinwheel_api_script)
  },
}
</script>

<style lang="scss">
iframe:not(.md-image) {
  height: 100%;
}

.pinwheel-portal.pinwheel-modal-show {
  height: 100%;
}
</style>

<style lang="scss" scoped>
@import '@/styles/grant-circles';
@import '@/styles/mixins/responsive';

.verification {
  display: flex;
  flex-direction: column;

  .verify-form {
    max-width: 453px;
    width: 100%;

    align-self: center;

    @extend .card-shape;

    .verify-form-footer {
      text-align: center;

      .sign-in-text {
        line-height: 1rem;
      }
    }
  }

  .form-header {
    margin-bottom: 0;
  }

  .sub-org-select-form-header {
    align-self: center;
  }

  .sub-org-select-form {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-self: center;
    justify-content: space-evenly;

    @include tablet-and-up-screen {
      flex-direction: row;
      width: 100%;
      max-width: 800px;
      margin-top: 1.5rem;
    }

    .sub-org {
      @extend .card-shape;

      display: flex;
      cursor: pointer;
      align-content: center;
      justify-content: center;
      margin-bottom: 1.5rem;

      @include tablet-and-up-screen {
        height: 90px;
      }

      &:hover {
        background-color: #ececec;
      }

      &:last-of-type {
        margin-bottom: 0;
      }

      span {
        text-decoration: underline;
      }
    }
  }
}
</style>
